import { formatPhoneNumber } from '~/lib/utils';
import { UserIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { GET_USER } from '~/services/user';
import Skeleton from 'react-loading-skeleton';
import { useCallback, useState } from 'react';
import {
  CLOSE_CONVERSATION,
  LIST_ACTIVITIES,
  GET_USER_ACTIVITY,
} from '~/services';
import { useTranslation } from 'react-i18next';

export const MessagesHeader = ({
  isSmallDevice,
}: {
  isSmallDevice: boolean;
}) => {
  const { personId, agentId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationId = searchParams.get('conversationId');

  const { data, loading } = useQuery(GET_USER, {
    variables: {
      data: {
        id: personId,
        channel_type: 'whatsapp,instagram',
      },
    },
    skip: !personId,
  });
  const navigate = useNavigate();
  const user = data?.get_user.data;
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const apollo = useApolloClient();
  const [closeConversation, { loading: closing }] = useMutation(
    CLOSE_CONVERSATION,
    {
      onCompleted: () => {
        // Clear conversationId from URL after closing
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('conversationId');
        newSearchParams.set('hasOpenConversation', 'false');
        setSearchParams(newSearchParams);
        // Refetch activities list so the sidebar updates
        apollo.refetchQueries({
          include: [LIST_ACTIVITIES, GET_USER_ACTIVITY],
        });
        setIsConfirmOpen(false);
      },
    },
  );
  const [t] = useTranslation('conversations');

  const handleCloseView = useCallback(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('conversationId');
    setSearchParams(newSearchParams);
  }, [searchParams, setSearchParams]);

  const confirmCloseConversation = useCallback(() => {
    if (!conversationId) return;
    closeConversation({
      variables: {
        data: {
          conversation_id: conversationId,
        },
      },
    });
  }, [closeConversation, conversationId]);

  if (loading)
    return (
      <div className="border-base-300 bg-custom-surface-50 flex h-[9vh]  w-full flex-col border-b px-4 py-2">
        <Skeleton width={20} height={20} />
        <Skeleton width={100} height={20} />
      </div>
    );

  if (!user) return null;

  return (
    <div className="border-base-300 bg-custom-surface-50 flex h-[9vh] w-full items-center justify-between border-b px-4 py-2">
      <div className="flex items-center gap-2">
        {isSmallDevice && (
          <XMarkIcon
            className="text-custom-primary-accent-400 h-6 w-6 hover:cursor-pointer"
            onClick={() => {
              navigate(`/agent/${agentId}/conversations`);
            }}
          />
        )}
        <div className="flex flex-col gap-1">
          <p className="text-base-content text-sm font-bold capitalize">
            {user?.profile_name ?? 'Desconocido'}
          </p>
          <p className="text-xs">
            {user?.channel_type === 'whatsapp' ? (
              formatPhoneNumber(user.channel_id)
            ) : user?.channel_type === 'instagram' && user.channel_id ? (
              <a
                href={`https://www.instagram.com/${user.profile_name.substring(
                  1,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                {'Instagram'}
              </a>
            ) : (
              user?.channel_id || 'Instagram' // Display channel_id or empty string for other types
            )}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {conversationId && (
          <button
            onClick={() => setIsConfirmOpen(true)}
            className="btn btn-sm btn-primary rounded-full"
            aria-label={t('close_conversation')}
            disabled={closing}
          >
            {closing ? 'Closing…' : t('close_conversation')}
          </button>
        )}
        {conversationId && (
          <button
            onClick={handleCloseView}
            className="rounded-full p-1 hover:bg-gray-200 dark:hover:bg-gray-700"
            aria-label="Close conversation view"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </button>
        )}
      </div>

      {isConfirmOpen && (
        <dialog open className="modal">
          <div className="modal-box">
            <h3 className="text-lg font-bold">
              {t('close_conversation_title')}
            </h3>
            <p className="py-2 text-sm">
              {t('close_conversation_description')}
            </p>
            <div className="modal-action">
              <button
                className="btn btn-ghost rounded-full"
                onClick={() => setIsConfirmOpen(false)}
                disabled={closing}
              >
                {t('cancel')}
              </button>
              <button
                className="btn btn-primary rounded-full"
                onClick={confirmCloseConversation}
                disabled={closing}
              >
                {closing ? 'Closing…' : t('confirm')}
              </button>
            </div>
          </div>
        </dialog>
      )}
    </div>
  );
};
